/*
 * Single-select dropdown for Categories, using the current category as the trigger button
 */
import { Category } from '@range.io/basic-types'
import { tagged } from '@range.io/functional'
import PropTypes from 'prop-types'
import React from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import Icon from '../components-reusable/Icon.js'
import { Button, Link, Text, Tooltip } from '../components-reusable/index.js'
import SingleSelectWithFilter, { StyledButton, StyledItemLabel } from '../components-reusable/SingleSelectWithFilter.js'
import { RangeUITheme } from '../range-theme/index.js'
import { ReduxSelectors } from '../redux/index.js'

/*
 * Render the icon for a Category, possibly changing its size and color
 * Hack: category is actually the TEXT of an SVG that we convert to a span with an inner <svg> component
 *
 * @sig CategoryIcon :: ({ category: Category, size = Number, color = String }) -> ReactComponent
 */
const CategoryIcon = ({ category, size = 20, color = 'currentColor' }) => {
    // replace the width and height embedded in the SVG text from category.icon with the passed-in size
    // width='40' height='50' -> width="20" height="20"
    const svgContent = atob(category.icon.split(',')[1])
    const htmlText = svgContent
        .replace(/width=['"][^'"]*['"]/, `width="${size}"`)
        .replace(/height=['"][^'"]*['"]/, `height="${size}"`)

    // HACK: can't use stitches because we're using 'style'
    if (color[0] === '$') {
        const colorName = color.slice(1, 2).toUpperCase() + color.slice(2) // $neutral04 -> Neutral04
        color = RangeUITheme[colorName]
    }

    // embed our now-modified SVG text into a span, also setting the style of the span
    return (
        <span
            dangerouslySetInnerHTML={{ __html: htmlText }}
            style={{ width: size, height: size, color, display: 'inline-block' }}
        />
    )
}

const renderItem = (isSelected, category) => (
    <StyledButton
        style={{ justifyContent: 'flex-start' }}
        variant="dropdown-menuitem-navigator"
        data-selected={isSelected}
        data-cy="list-item-category"
    >
        <CategoryIcon category={category} />
        <StyledItemLabel css={{ paddingLeft: 6 }}>{category.name}</StyledItemLabel>
    </StyledButton>
)

/*
 * An additional link that can be added to the bottom of a SingleSelectWithFilter
 */

const EditCategoriesLink = () => {
    const handleEditCategoriesClicked = () =>
        navigate(`/${workspaceId}/${projectId}/categories`, { state: { safeToNavigateBack: true } })

    const navigate = useNavigate()
    const { workspaceId, projectId } = useParams()

    return (
        <Link
            css={{
                lineHeight: '18px',
                padding: '4px 6px 4px 6px',
                borderRadius: 6,
                width: 'auto',
                '&:hover': { background: '$primary02', color: '$primary06' },
            }}
            onClick={handleEditCategoriesClicked}
        >
            Edit Categories
        </Link>
    )
}

/*
 * Single-select dropdown for categories, including the selected category as the trigger
 * @sig CategorySelector :: { categories: LookupTable, selectedCategory: Category, setSelectedCategory: Func } -> React
 */
const CategorySelector = ({ selectedCategory, setSelectedCategory, collaboration }) => {
    const categories = useSelector(ReduxSelectors.categories)
    const mayUpdateCategories = useSelector(ReduxSelectors.isUpdateAllowed('category'))
    const canUpdateCollaboration = useSelector(ReduxSelectors.isUpdateAllowed('collaboration', collaboration))

    const renderTrigger = category => (
        <StyledButton
            variant="categories"
            data-selected={false}
            style={{ backgroundColor: '$neutral03', marginRight: 'auto' }}
        >
            <CategoryIcon category={category} />
            <StyledItemLabel>{category.name}</StyledItemLabel>
            <Icon iconSize="16px" name={canUpdateCollaboration ? 'chevronDown' : 'lock'} />
        </StyledButton>
    )

    const _setSelectedItem = id => setSelectedCategory(categories.get(id))

    // If user can't update collaboration, render just the trigger button without dropdown functionality
    if (!canUpdateCollaboration) {
        return (
            <Tooltip tooltipText="Category locked - no permission to edit">{renderTrigger(selectedCategory)}</Tooltip>
        )
    }

    return (
        <SingleSelectWithFilter
            items={categories}
            selectedItem={selectedCategory}
            placeholder={'Search for a category'}
            tooltipText={'Select a category'}
            setSelectedItem={_setSelectedItem}
            renderItem={renderItem}
            renderTrigger={renderTrigger}
            ExtraLink={mayUpdateCategories && EditCategoriesLink}
        />
    )
}

/*
 * Category selector whose trigger matches the style of the bulk-editing toolbar
 * @sig BulkCategorySelector :: { setSelectedCategory: Func } -> React
 */
const BulkCategorySelector = ({ setSelectedCategory }) => {
    const _setSelectedItem = id => setSelectedCategory(categories.get(id))

    const renderTrigger = item => (
        <Button variant="secondary" size="lg">
            <Icon name="unassigned" iconSize="16px" />
            <Text>Category</Text>
        </Button>
    )

    const categories = useSelector(ReduxSelectors.categories)
    const mayUpdateCategories = useSelector(ReduxSelectors.isUpdateAllowed('category'))

    return (
        <SingleSelectWithFilter
            items={categories}
            placeholder={'Search for a category'}
            tooltipText={'Select a category'}
            setSelectedItem={_setSelectedItem}
            renderItem={renderItem}
            renderTrigger={renderTrigger}
            ExtraLink={mayUpdateCategories && EditCategoriesLink}
        />
    )
}

CategorySelector.propTypes = {
    selectedCategory: tagged.typeIsRequired(Category),
    setSelectedCategory: PropTypes.func.isRequired,
    collaboration: PropTypes.object.isRequired,
}

BulkCategorySelector.propTypes = {
    setSelectedCategory: PropTypes.func.isRequired,
}

export { CategoryIcon, CategorySelector, BulkCategorySelector }
